import { useMemo, useSyncExternalStore } from "react";
import { fetchPrimary } from "../environments/primary/fetch";
import { isWebHost } from "../webHost";
import { watchPrimary } from "./watchPrimary";
import { watchWindowNotifications } from "./webHostNotificationWatch";
import { useNeucleosWorldStore } from "./worldStore";

export type NotificationState = "unread" | "read" | "dismissed";
export type NotificationRead = {
  kind: "gate" | "reminder" | "activity";
  id: string;
  revision: string;
  state: NotificationState;
};
export type NotificationItem = {
  id: string;
  revision: string;
  label: string;
  readState: NotificationState;
};
export type NotificationSnapshot = {
  actorId: string;
  pendingGateCount?: number | null;
  unreadGateCount?: number | null;
  gates: NotificationItem[] | null;
  reminders: NotificationItem[] | null;
  activityReads: Omit<NotificationRead, "kind">[];
};
type Snapshot = { value: NotificationSnapshot | null; error: string | null };
const empty: Snapshot = { value: null, error: null };
const channels = new Map<string, ReturnType<typeof createChannel>>();
function createChannel() {
  const listeners = new Map<string, Set<() => void>>();
  const snapshots = new Map<string, Snapshot>();
  const refused = new Set<string>();
  let controller: AbortController | null = null;
  let generation = 0;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let backoff = 1000;
  let signature = "";
  let scheduled = false;
  const keys = () => [...listeners.keys()].filter((key) => !refused.has(key)).sort();
  const publish = (worldKey: string, value: Snapshot) => {
    snapshots.set(worldKey, value);
    for (const fn of listeners.get(worldKey) ?? []) fn();
  };
  const current = (cut: number) =>
    cut === generation && listeners.size > 0 && document.visibilityState !== "hidden";
  const stop = () => {
    generation++;
    controller?.abort();
    controller = null;
    clearTimeout(retry);
  };
  const connect = async () => {
    const worldKeys = keys();
    const cut = generation;
    if (!current(cut) || controller || !worldKeys.length) return;
    const active = new AbortController();
    controller = active;
    try {
      // cleo-web (7.4.4a): no Mac server streams the feed; the window watches the World itself, in the same lines.
      const response = isWebHost()
        ? await watchWindowNotifications({ worldKeys }, active.signal)
        : await watchPrimary({ kind: "notifications", worldKeys }, active.signal);
      if (!current(cut)) return;
      if ([401, 403, 409].includes(response.status)) {
        for (const key of worldKeys) refused.add(key);
        throw new Error("Notification access unavailable. Check Settings → Account.");
      }
      if (!response.ok || !response.body) throw new Error("Notifications are reconnecting.");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      try {
        while (current(cut)) {
          const part = await reader.read();
          if (!current(cut)) break;
          if (part.done) throw new Error("Notifications are reconnecting.");
          buffer += decoder.decode(part.value, { stream: true });
          if (buffer.length > 1024 * 1024) throw new Error("Invalid notification response.");
          let end: number;
          while ((end = buffer.indexOf("\n")) >= 0) {
            const event = JSON.parse(buffer.slice(0, end));
            buffer = buffer.slice(end + 1);
            if (!worldKeys.includes(event.worldKey)) throw new Error("Invalid notification World.");
            if (event.kind === "refused") {
              refused.add(event.worldKey);
              publish(event.worldKey, {
                value: null,
                error: "Notification access unavailable. Check Settings → Account.",
              });
              continue;
            }
            if (event.kind !== "changed" || typeof event.value?.actorId !== "string")
              throw new Error("Notifications are reconnecting.");
            if (!refused.has(event.worldKey)) {
              backoff = 1000;
              publish(event.worldKey, { value: event.value, error: null });
            }
          }
        }
      } finally {
        await reader.cancel().catch(() => undefined);
      }
    } catch (cause) {
      if (cut === generation && !active.signal.aborted)
        for (const key of worldKeys)
          publish(key, {
            value: null,
            error: cause instanceof Error ? cause.message : "Notifications unavailable.",
          });
    } finally {
      if (controller === active) controller = null;
      if (current(cut) && keys().length) {
        retry = setTimeout(() => void connect(), backoff);
        backoff = Math.min(30000, backoff * 2);
      }
    }
  };
  const reconcile = () => {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      const next = JSON.stringify(keys());
      if (next === signature) return;
      signature = next;
      stop();
      void connect();
    });
  };
  const visibility = () => {
    stop();
    for (const key of listeners.keys()) publish(key, empty);
    if (document.visibilityState !== "hidden") void connect();
  };
  return {
    getSnapshot: (worldKey: string) => snapshots.get(worldKey) ?? empty,
    unused: () => listeners.size === 0,
    subscribe(worldKey: string, fn: () => void) {
      if (!listeners.size) document.addEventListener("visibilitychange", visibility);
      let group = listeners.get(worldKey);
      if (!group) {
        group = new Set();
        listeners.set(worldKey, group);
      }
      group.add(fn);
      reconcile();
      return () => {
        group.delete(fn);
        if (!group.size) listeners.delete(worldKey);
        if (!listeners.size) {
          document.removeEventListener("visibilitychange", visibility);
          stop();
        }
        reconcile();
      };
    },
  };
}

/** One browser stream carries all observed Worlds; each keeps its own grant and receipt state. */
export function useNotificationSnapshots(worldKeys: readonly string[]) {
  const revision = useNeucleosWorldStore((state) => state.grantRevision);
  const identity = useNeucleosWorldStore((state) => state.identity);
  const keys = JSON.stringify([...new Set(worldKeys)].sort());
  const group = useMemo(() => {
    const channelKey = JSON.stringify([revision, identity]);
    let channel = channels.get(channelKey);
    if (!channel) {
      channel = createChannel();
      channels.set(channelKey, channel);
    }
    const selected = JSON.parse(keys) as string[];
    const read = () => selected.map((worldKey) => ({ worldKey, ...channel.getSnapshot(worldKey) }));
    let values = read();
    return {
      getSnapshot: () => values,
      subscribe: (fn: () => void) => {
        const change = () => {
          values = read();
          fn();
        };
        const stops = selected.map((worldKey) => channel.subscribe(worldKey, change));
        change();
        return () => {
          for (const stop of stops) stop();
          queueMicrotask(() => {
            if (channel.unused() && channels.get(channelKey) === channel)
              channels.delete(channelKey);
          });
        };
      },
    };
  }, [keys, revision, identity]);
  return useSyncExternalStore(group.subscribe, group.getSnapshot, group.getSnapshot);
}

/** The server saves through the preference door and independently reads back. */
export async function markNotifications(
  worldKey: string,
  actorId: string,
  rows: NotificationRead[],
) {
  for (let offset = 0; offset < rows.length; offset += 64) {
    const response = await fetchPrimary("/api/neucleos/notifications/read", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        worldKey,
        actorId,
        notificationReads: rows.slice(offset, offset + 64),
      }),
    });
    const value = await response.json();
    if (!response.ok || value?.ok !== true)
      throw new Error(value?.reason ?? "Notification read state could not be saved.");
  }
}
